/**
 * Admin products API layer (STORY-008-007). Typed calls to the SHIPPED
 * STORY-003-001 admin marketplace router (`/admin/marketplace/products*`). This
 * story adds NO backend routes.
 *
 * Types mirror `ProductResponse` / `ProductUpdate` / `BulkStockStateRequest` /
 * `BulkSoftDeleteRequest` / `BulkResultResponse` in
 * `apps/api/garden/features/marketplace/schemas.py`. Money (`price_kgs`,
 * `bulk_price_kgs`) is a Decimal serialised as a JSON **string** (never tiyin);
 * the UI keeps it a string to the API boundary.
 */
import { apiClient, apiFetch } from "@/lib/api-client";
import type {
  AttributeValueIn,
  AttributeValueOut,
} from "@/features/seller-dashboard/attributes/formSchema";

/** Product `stock_state` (frozen CHECK). */
export const PRODUCT_STOCK_STATES = [
  "in_stock",
  "low",
  "out_of_stock",
  "unknown",
] as const;
export type ProductStockState = (typeof PRODUCT_STOCK_STATES)[number];

/** Product `status` (frozen CHECK — 6 values). */
export const PRODUCT_STATUSES = [
  "draft",
  "pending_approval",
  "active",
  "rejected",
  "soft_deleted",
  "hidden_by_seller",
] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

/** One image on the admin editor surface (mirrors the admin
 * `ProductResponse.images` entry — same shape as the seller's
 * `SellerProductImage`, since the backend reuses `SellerProductImageDto`
 * for both surfaces). */
export interface AdminProductImage {
  id: string;
  s3_key_original: string;
  s3_key_200: string | null;
  s3_key_600: string | null;
  s3_key_1200: string | null;
  sort_order: number;
  url: string;
  thumb_url: string;
}

/** One product row (mirrors `ProductResponse`). Money fields are strings.
 * `images`/`attributes`/`category_ids` use `Field(default_factory=list)`
 * server-side (always present, never null); `primary_category_id` is
 * forced to always mirror `category_id` by a model validator (Task B3);
 * only `review_reason` is genuinely nullable. */
export interface AdminProduct {
  id: string;
  seller_id: string;
  /** SCRUM-164: the seller's business name, joined server-side — populated
   * ONLY on the list read (`GET /admin/marketplace/products`); `null` on
   * every other response (create/patch/detail/bulk echoes) and, in theory,
   * for a product whose seller row is missing (outer join). */
  seller_business_name: string | null;
  category_id: string;
  name: string;
  description: string | null;
  price_kgs: string | null;
  bulk_price_kgs: string | null;
  bulk_threshold: number | null;
  stock_state: string;
  status: string;
  variant_group_id: string | null;
  /** [] on list rows; populated on the single-product detail read. */
  images: AdminProductImage[];
  attributes: AttributeValueOut[];
  category_ids: string[];
  primary_category_id: string | null;
  /** Latest send-back reason; only populated on the detail read. */
  review_reason: string | null;
  /** v0048 workbook properties. Optional so a cached older shape stays
   * assignable; `tags`/`folk_names` are populated on the DETAIL read only. */
  unit?: string | null;
  brand?: string | null;
  article?: string | null;
  article_source?: string | null;
  formal_name?: string | null;
  price_max_kgs?: string | null;
  variant_count?: number;
  tags?: string[];
  folk_names?: string[];
}

/** Cursor-paginated list (mirrors `ProductListResponse`). */
export interface AdminProductListResponse {
  products: AdminProduct[];
  next_cursor: string | null;
}

/** A photo to attach on PATCH (mirrors the backend `SellerPhotoAttachment`,
 * reused by the admin `ProductUpdate.photos` field per Task B1). */
export interface AdminPhotoAttachment {
  s3_key_original: string;
  sort_order: number;
}

/** PATCH body (subset of `ProductUpdate` this UI edits). */
export interface AdminProductUpdate {
  name?: string;
  description?: string | null;
  category_id?: string;
  status?: string;
  stock_state?: string;
  price_kgs?: string | null;
  bulk_price_kgs?: string | null;
  bulk_threshold?: number | null;
  photos?: AdminPhotoAttachment[];
  attributes?: AttributeValueIn[];
  category_ids?: string[];
  primary_category_id?: string | null;
  /** SCRUM-197: admin reassignment. Mirrors `ProductUpdate.seller_id`
   * (`schemas.py:283`); validated server-side via `seller_exists` (422
   * `ProductSellerNotFoundError`) and applied in the `setattr` loop
   * (`service.py:1109-1111`, `:1133`). Sent ONLY when the admin actually
   * changed the seller — see `ProductEditor.tsx`'s `buildBody`. */
  seller_id?: string;
}

/** Create body (mirrors the backend `ProductCreate` minus `variant_group_id`
 * — the editor never sets that field). `seller_id` is REQUIRED here (unlike
 * the seller-self endpoint, the admin endpoint always needs an explicit
 * seller). */
export interface AdminProductCreate {
  seller_id: string;
  category_id: string;
  name: string;
  description?: string | null;
  price_kgs?: string | null;
  bulk_price_kgs?: string | null;
  bulk_threshold?: number | null;
  stock_state?: string;
  status?: string;
  attributes?: AttributeValueIn[];
  category_ids?: string[];
  primary_category_id?: string | null;
}

export function createProduct(body: AdminProductCreate): Promise<AdminProduct> {
  return apiClient.post<AdminProduct, AdminProductCreate>(
    "/admin/marketplace/products",
    body,
  );
}

export function reorderPhotos(
  id: string,
  orderedImageIds: string[],
): Promise<AdminProduct> {
  return apiClient.post<AdminProduct, { ordered_image_ids: string[] }>(
    `/admin/marketplace/products/${id}/photos/reorder`,
    { ordered_image_ids: orderedImageIds },
  );
}

/** The subset of `SellerResponse` the picker actually needs (the backend
 * returns the FULL seller row; extra fields are simply ignored client-side
 * — no runtime validation is performed on read). */
export interface AdminSellerSummary {
  id: string;
  business_name: string;
}

export interface SearchSellersResponse {
  sellers: AdminSellerSummary[];
  next_cursor: string | null;
}

export function searchSellers(
  q: string,
  limit = 20,
): Promise<SearchSellersResponse> {
  const query = new URLSearchParams();
  if (q.trim() !== "") query.set("q", q.trim());
  query.set("limit", String(limit));
  return apiClient.get<SearchSellersResponse>(
    `/admin/marketplace/sellers?${query.toString()}`,
  );
}

/** One admin variant row (mirrors `VariantResponse` — matches the shipped
 * seller variant API exactly, see `seller-dashboard/api.ts`'s
 * `listSellerVariants`/`createSellerVariant`/`deleteSellerVariant`). */
export interface AdminVariant {
  id: string;
  product_id: string;
  variant_attribute_key: string;
  variant_attribute_value: string;
}

export interface AdminVariantListResponse {
  variants: AdminVariant[];
}

export function listVariants(productId: string): Promise<AdminVariantListResponse> {
  return apiClient.get<AdminVariantListResponse>(
    `/admin/marketplace/products/${productId}/variants`,
  );
}

export function createVariant(
  productId: string,
  key: string,
  value: string,
): Promise<AdminVariant> {
  return apiClient.post<
    AdminVariant,
    { variant_attribute_key: string; variant_attribute_value: string }
  >(`/admin/marketplace/products/${productId}/variants`, {
    variant_attribute_key: key,
    variant_attribute_value: value,
  });
}

export function deleteVariant(
  productId: string,
  variantId: string,
): Promise<void> {
  return apiClient.delete<void>(
    `/admin/marketplace/products/${productId}/variants/${variantId}`,
  );
}

/** Bulk result (mirrors `BulkResultResponse`). */
export interface BulkResultResponse {
  affected: string[];
}

export const productKeys = {
  list: (filters: Record<string, unknown>) =>
    ["admin", "products", filters] as const,
  detail: (id: string) => ["admin", "products", "detail", id] as const,
} as const;

export interface ListProductsParams {
  seller_id?: string | null;
  category_id?: string | null;
  stock_state?: string | null;
  cursor?: string | null;
  limit?: number;
}

export function listProducts(
  params: ListProductsParams,
): Promise<AdminProductListResponse> {
  const query = new URLSearchParams();
  if (params.seller_id) query.set("seller_id", params.seller_id);
  if (params.category_id) query.set("category_id", params.category_id);
  if (params.stock_state) query.set("stock_state", params.stock_state);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<AdminProductListResponse>(
    `/admin/marketplace/products${qs ? `?${qs}` : ""}`,
  );
}

export function getProduct(id: string): Promise<AdminProduct> {
  return apiClient.get<AdminProduct>(`/admin/marketplace/products/${id}`);
}

export function patchProduct(
  id: string,
  body: AdminProductUpdate,
): Promise<AdminProduct> {
  return apiFetch<AdminProduct, AdminProductUpdate>({
    method: "PATCH",
    path: `/admin/marketplace/products/${id}`,
    body,
  });
}

export function softDeleteProduct(id: string): Promise<AdminProduct> {
  return apiClient.delete<AdminProduct>(`/admin/marketplace/products/${id}`);
}

export function bulkSetStock(
  productIds: string[],
  stockState: string,
): Promise<BulkResultResponse> {
  return apiClient.post<
    BulkResultResponse,
    { product_ids: string[]; stock_state: string }
  >("/admin/marketplace/products:bulk-stock-state", {
    product_ids: productIds,
    stock_state: stockState,
  });
}

export function bulkSoftDelete(
  productIds: string[],
): Promise<BulkResultResponse> {
  return apiClient.post<BulkResultResponse, { product_ids: string[] }>(
    "/admin/marketplace/products:bulk-soft-delete",
    { product_ids: productIds },
  );
}

/**
 * `POST /admin/marketplace/products:bulk-set-attribute` (PIM Wave P5 Task 6
 * backend, Task 8 FE). Mirrors `bulk_attributes.py`'s
 * `BulkSetAttributeRequest`/`BulkSetAttributeResponse` EXACTLY — a RICHER
 * shape than the sibling bulk endpoints' `BulkResultResponse` (per-product
 * isolation report: `updated[]` + `failed[{product_id, reason}]`), so this
 * gets its OWN response type rather than reusing `BulkResultResponse`.
 */
export interface BulkSetAttributeFailure {
  product_id: string;
  reason: string;
}

export interface BulkSetAttributeResponse {
  updated: string[];
  failed: BulkSetAttributeFailure[];
}

/** The typed attribute value on the wire — `Any` server-side, but every
 * caller here sends exactly one of these four JSON-native shapes (`null`
 * clears the attribute). */
export type BulkAttributeValue = string | number | boolean | null;

export function bulkSetAttribute(
  productIds: string[],
  fieldSlug: string,
  value: BulkAttributeValue,
  unit?: string | null,
): Promise<BulkSetAttributeResponse> {
  return apiClient.post<
    BulkSetAttributeResponse,
    { product_ids: string[]; field_slug: string; value: BulkAttributeValue; unit: string | null }
  >("/admin/marketplace/products:bulk-set-attribute", {
    product_ids: productIds,
    field_slug: fieldSlug,
    value,
    unit: unit ?? null,
  });
}
